import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import Dropdown from '../../../../components/UI/Dropdown/Dropdown';
import Button from '../../../../components/UI/Button/Button';
import styles from './PurchaseBusinessPageStageThree.module.css';
import { addDays, format, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import ScalePreview from '../../../../components/UI/ScalePreview/ScalePreview';
import Icon from '../../../../components/Icon';
import { sendLeadToWhatsApp } from '../../../../utils/whatsapp';
import cn from 'classnames';

const YANDEX_MAP_API_URL = 'https://api-maps.yandex.ru/2.1/?lang=ru_RU';

const PurchaseBusinessPageStageThree: React.FC = () => {
    const [zoomLevel, setZoomLevel] = useState(160);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<any>(null);
    const centerCoordinates = useMemo(() => [42.899008, 47.621958], []);
    const [selectedDateValue, setSelectedDateValue] = useState<string | null>(null);
    const [selectedDateLabel, setSelectedDateLabel] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [currentHour, setCurrentHour] = useState(new Date().getHours());
    const [errors, setErrors] = useState<{ date: boolean; time: boolean }>({ date: false, time: false });

    useEffect(() => {
        const scriptExists = document.querySelector(`script[src="${YANDEX_MAP_API_URL}"]`);
        if (!scriptExists) {
            const script = document.createElement('script');
            script.src = YANDEX_MAP_API_URL;
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    useEffect(() => {
        if (mapRef.current && window.ymaps) {
            window.ymaps.ready(() => {
                if (!mapInstance.current) {
                    const initialZoom = 2 + (zoomLevel - 2) * (20 - 2) / (200 - 10);
                    mapInstance.current = new window.ymaps.Map(mapRef.current, {
                        center: centerCoordinates,
                        zoom: initialZoom,
                        controls: []
                    });

                    const handleMouseWheel = (e: WheelEvent) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -10 : 10;
                        setZoomLevel((prevZoom) => {
                            const newZoom = prevZoom + delta;
                            return Math.max(10, Math.min(newZoom, 200));
                        });
                    };

                    if (mapRef.current) {
                        mapRef.current.addEventListener('wheel', handleMouseWheel, { passive: false });
                    }

                    return () => {
                        if (mapRef.current) {
                            mapRef.current.removeEventListener('wheel', handleMouseWheel);
                        }
                    };
                }
            });
        }
    }, [mapRef, centerCoordinates, zoomLevel]);

    useEffect(() => {
        if (mapInstance.current) {
            const yandexZoomLevel = 2 + (zoomLevel - 2) * (20 - 2) / (200 - 10);
            mapInstance.current.setZoom(yandexZoomLevel, {
                duration: 300,
                timingFunction: 'ease-in-out'
            });
        }
    }, [zoomLevel]);

    const getNextThreeDates = () => {
        const now = new Date();
        const nowHour = now.getHours();
        const currentMinutes = now.getMinutes();
        const startDayOffset = (nowHour > 16 || (nowHour === 16 && currentMinutes >= 30)) ? 1 : 0;

        return Array.from({ length: 3 }, (_, index) => {
            const nextDate = addDays(now, index + startDayOffset);
            let label = `${format(nextDate, 'dd', { locale: ru })} ${format(nextDate, 'MMMM', { locale: ru })}`;

            if (index === 0 && startDayOffset === 0) {
                label += ', до 18:30';
            }

            return {
                value: format(nextDate, 'dd.MM.yyyy', { locale: ru }),
                label
            };
        });
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const newHour = new Date().getHours();
            setCurrentHour((prevHour) => (prevHour !== newHour ? newHour : prevHour));
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    const dateOptions = useMemo(() => getNextThreeDates(), [currentHour]);
    const officeOptions = useMemo(() => [
        { value: 'omarova', label: 'Офис на Омарова, 8' },
        { value: 'bayramova', label: 'Офис на Байрамова' }
    ], []);

    const [selectedOffice, setSelectedOffice] = useState<string>(officeOptions[0].value);

    const officeLocations = useMemo(() => ({
        omarova: {
            coords: [42.899008, 47.621958],
            route: 'https://yandex.ru/maps/11008/kaspiysk/?ll=47.622042%2C42.898893&mode=routes&rtext=~42.898949%2C47.621987&rtt=auto&ruri=~ymapsbm1%3A%2F%2Forg%3Foid%3D117585465488&z=18'
        },
        bayramova: {
            coords: [42.886719, 47.623276],
            route: 'https://yandex.ru/maps/11008/kaspiysk/?ll=47.623595%2C42.886733&mode=routes&rtext=~42.886716%2C47.623284&rtt=auto&ruri=~ymapsbm1%3A%2F%2Forg%3Foid%3D224055611453&z=19.93'
        }
    }), []);

    useEffect(() => {
        if (mapInstance.current) {
            mapInstance.current.panTo(officeLocations[selectedOffice as keyof typeof officeLocations].coords, {
                duration: 500,
                timingFunction: 'ease-out'
            });
        }
    }, [selectedOffice, officeLocations]);

    const handleLocationClick = () => {
        if (mapInstance.current) {
            mapInstance.current.panTo(officeLocations[selectedOffice as keyof typeof officeLocations].coords, {
                duration: 500,
                timingFunction: 'ease-out'
            });
        }
    };

    const handleRouteClick = () => {
        window.open(officeLocations[selectedOffice as keyof typeof officeLocations].route, '_blank');
    };

    const handleDateSelect = (option: { value: string; label: string } | null) => {
        setSelectedDateValue(option?.value ?? null);
        setSelectedDateLabel(option?.label ?? null);
        setSelectedTime(null);
        setErrors({ date: false, time: false });
    };

    const getTimeOptions = useCallback((dateValue: string | null) => {
        const now = new Date();
        const timeOptions = [];

        if (!dateValue) return [];

        const selectedDateObj = parse(dateValue, 'dd.MM.yyyy', new Date());
        if (isNaN(selectedDateObj.getTime())) {
            return [];
        }

        const isToday = selectedDateObj.toDateString() === now.toDateString();

        if (isToday) {
            const nowHour = now.getHours();
            const currentMinutes = now.getMinutes();

            if (nowHour < 16 || (nowHour === 16 && currentMinutes < 30)) {
                timeOptions.push(
                    { value: '16:30', label: '16:30' },
                    { value: '17:00', label: '17:00' },
                    { value: '17:30', label: '17:30' },
                    { value: '18:00', label: '18:00' }
                );
            }
        } else {
            let timeHour = 9;
            let currentMinute = 30;

            while (timeHour < 18 || (timeHour === 18 && currentMinute === 0)) {
                const timeString = `${timeHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
                timeOptions.push({ value: timeString, label: timeString });

                currentMinute += 30;
                if (currentMinute >= 60) {
                    currentMinute = 0;
                    timeHour += 1;
                }
            }
        }

        return timeOptions;
    }, []);

    const timeOptions = useMemo(() => getTimeOptions(selectedDateValue), [selectedDateValue, getTimeOptions]);

    const handleContinue = () => {
        const nextErrors = {
            date: !selectedDateValue,
            time: !selectedTime,
        };

        if (nextErrors.date || nextErrors.time) {
            setErrors(nextErrors);
            return;
        }

        let bookingData: { ownerType?: string; fullName?: string; phoneNumber?: string } = {};
        try {
            const raw = localStorage.getItem('bookingLeadBusiness');
            if (raw) {
                bookingData = JSON.parse(raw);
            }
        } catch {
            bookingData = {};
        }

        sendLeadToWhatsApp({
            title: 'Бронирование коммерции',
            fields: [
                { label: 'Тип собственника', value: bookingData.ownerType },
                { label: 'Имя и фамилия', value: bookingData.fullName },
                { label: 'Телефон', value: bookingData.phoneNumber },
                { label: 'Дата встречи', value: selectedDateLabel },
                { label: 'Время встречи', value: selectedTime },
                { label: 'Офис', value: officeOptions.find((option) => option.value === selectedOffice)?.label },
            ],
        });

        localStorage.removeItem('bookingLeadBusiness');
    };

    return (
        <>
            <div className={styles.text_wrapper}>
                <h3>Назначение встречи</h3>
                <p className='text_body_b2'>Договор оформляется у&nbsp;нас в&nbsp;офисе на&nbsp;проспекте Омарова, 8. С&nbsp;собой необходимо будет взять паспорт и&nbsp;средства для оплаты.</p>
            </div>
            <div className={styles.date_pick_wrapper}>
                <div className={styles.dropdowns_wrapper}>
                    <Dropdown
                        options={dateOptions}
                        placeholder='Дата встречи'
                        selected={dateOptions.find((option) => option.value === selectedDateValue) || null}
                        onSelect={handleDateSelect}
                        className={errors.date ? styles.error_field : ''}
                    />
                    {errors.date && <p className={cn('text_captions_c1', styles.error_text)}>Выберите дату встречи</p>}
                    {selectedDateValue && (
                        <Dropdown
                            options={timeOptions}
                            placeholder='Выбор времени'
                            selected={timeOptions.find((option) => option.value === selectedTime) || null}
                            onSelect={(option) => {
                                setSelectedTime(option?.value ?? null);
                                setErrors((prev) => ({ ...prev, time: false }));
                            }}
                            className={errors.time ? styles.error_field : ''}
                        />
                    )}
                    {errors.time && <p className={cn('text_captions_c1', styles.error_text)}>Выберите время встречи</p>}
                </div>
                <div className={styles.date_pick_info_wrapper}>
                    <div className={styles.date_pick_info_title}>
                        <p className={'text_subheaders_sh1'}>Выбор офиса</p>
                    </div>
                    <Dropdown
                        options={officeOptions}
                        placeholder='Выбор офиса'
                        selected={officeOptions.find((option) => option.value === selectedOffice) || null}
                        onSelect={(option) => setSelectedOffice(option?.value ?? officeOptions[0].value)}
                    />
                    <div ref={mapRef} className={styles.map_container}>
                        <div className={styles.controls_bar_map}>
                            <div className={styles.location_wrapper} onClick={handleLocationClick}>
                                <Icon name='locationIcon' width='8' height='8' />
                            </div>
                            <ScalePreview zoom={zoomLevel} setZoom={setZoomLevel} />
                        </div>
                    </div>
                    <Button colorType='gray' roundType='small_rounded' width='100%' onClick={handleRouteClick}>
                        Проложить маршрут
                    </Button>
                </div>
            </div>
            <div className={styles.fixed_button_wrapper}>
                <div className={styles.fixed_button}>
                    <Button
                        colorType='blue'
                        roundType='small_rounded'
                        width='100%'
                        onClick={handleContinue}
                    >
                        Продолжить
                    </Button>
                </div>
            </div>
        </>
    );
};

export default PurchaseBusinessPageStageThree;
